// src/components/avatar/wearableArt.js
//
// The starter wardrobe — drawn as SVG so it is crisp at any size, weighs
// nothing, and needs no image files. Each piece is drawn in a 100×100 box
// that AvatarWithOutfit places relative to the avatar circle (SLOT_BOX).
//
// STARTER_ITEMS is what the Layout Builder's "Add starter items" button
// writes into shopItems/{id}. A shop item can instead carry an imageUrl
// (an AI picture made in the Shop items panel); then `art` is ignored.
//
// Style: chunky outline (INK), flat bright fills, one highlight — the same
// friendly look as the lesson-video students.

import React from "react";
import { SEASON_ART } from "./seasons/seasonArt";

const INK = "#2a2130";
const S = { stroke: INK, strokeWidth: 3, strokeLinejoin: "round", strokeLinecap: "round" };

/**
 * Where each slot sits, in % of the avatar circle's size.
 * left/top are the box's corner; size is its width and height.
 * `behind` slots draw under the avatar picture.
 */
export const SLOT_BOX = {
  frame: { left: -12, top: -12, size: 124, behind: false },
  wings: { left: -42, top: -22, size: 184, behind: true },
  hat: { left: 12, top: -50, size: 76, behind: false },
  glasses: { left: 12, top: 4, size: 76, behind: false },
  pet: { left: 58, top: 50, size: 60, behind: false },
  pin: { left: 57, top: 72, size: 20, behind: false }, // custom shop pins (pictures) on the chest
  necklace: { left: 20, top: 52, size: 60, behind: false }, // around the neck, over the collar
};

/**
 * How much lower (in % of the avatar) a hat sits. Hats that COVER the head —
 * cones, top hats, caps, beanies, crowns — sat on top of the hair and looked
 * like they were floating; they now come down over it. Headbands (boppers,
 * ears, antlers, flower crowns) already sit right and stay where they are.
 */
const HEADBAND = /(bopper|ears|antler|horns|band|flower-crown|rose-crown|marigold|leaf-crown|harvest-crown)/;
export const hatDrop = (item) => {
  if (!item || item.kind !== "hat") return 0;
  const key = `${item.art || ""} ${item.id || ""}`.toLowerCase();
  if (HEADBAND.test(key)) return 0;
  if (item.imageUrl) return 4; // uploaded / AI pictures: a little
  return 7;
};

export const SLOT_ORDER = ["wings", "frame", "necklace", "glasses", "hat", "pin", "pet"];

export const SLOT_LABELS = {
  hat: "Hats",
  glasses: "Glasses",
  wings: "Wings",
  pet: "Pets",
  frame: "Frames",
  pin: "Pins",
  necklace: "Necklaces",
  powerup: "Power-ups",
};

/* ------------------------------------------------------------------ */
/* Hats — drawn sitting on the bottom of the box (head top = y≈100)     */
/* ------------------------------------------------------------------ */

const PartyHat = () => (
  <g {...S}>
    <path d="M50 18 L76 96 L24 96 Z" fill="#ff5d8f" />
    <path d="M37 56 L63 56 M31 76 L69 76" stroke="#ffd23f" strokeWidth="7" />
    <path d="M50 18 L76 96 L24 96 Z" fill="none" />
    <circle cx="50" cy="14" r="9" fill="#ffd23f" />
    <path d="M20 96 Q50 104 80 96" fill="none" />
  </g>
);

const BaseballCap = () => (
  <g {...S}>
    <path d="M18 92 Q18 46 52 44 Q84 44 84 90 Z" fill="#2a8bff" />
    <path d="M52 44 L52 90" stroke="#1c5fc0" strokeWidth="3" />
    <path d="M74 88 Q98 86 104 96 Q86 100 70 96 Z" fill="#1c5fc0" />
    <circle cx="52" cy="44" r="5" fill="#1c5fc0" />
    <circle cx="36" cy="70" r="9" fill="#fff" />
    <text x="36" y="75" textAnchor="middle" fontSize="13" fontWeight="900" fill="#2a8bff" stroke="none">S</text>
  </g>
);

const Crown = () => (
  <g {...S}>
    <path d="M16 94 L20 46 L36 66 L50 34 L64 66 L80 46 L84 94 Z" fill="#ffd23f" />
    <path d="M16 94 L84 94 L84 82 L16 82 Z" fill="#f5b301" />
    <circle cx="20" cy="44" r="5" fill="#ff5d8f" />
    <circle cx="50" cy="31" r="6" fill="#2ec4b6" />
    <circle cx="80" cy="44" r="5" fill="#ff5d8f" />
    <circle cx="50" cy="74" r="6" fill="#ff3b3b" />
    <path d="M30 56 L33 64" stroke="#fff6c7" strokeWidth="3" />
  </g>
);

const WizardHat = () => (
  <g {...S}>
    <path d="M52 6 Q60 40 78 82 L26 82 Q42 46 52 6 Z" fill="#6c4ce6" />
    <ellipse cx="52" cy="88" rx="44" ry="10" fill="#5536c9" />
    <path d="M38 60 l3 7 7 1 -5 5 1 7 -6 -4 -6 4 1 -7 -5 -5 7 -1 Z" fill="#ffd23f" strokeWidth="2" />
    <circle cx="60" cy="38" r="3" fill="#ffd23f" stroke="none" />
    <circle cx="64" cy="66" r="2.5" fill="#ffd23f" stroke="none" />
  </g>
);

const PirateHat = () => (
  <g {...S}>
    <path d="M8 86 Q20 40 50 40 Q80 40 92 86 Q50 74 8 86 Z" fill="#2b2b35" />
    <path d="M8 86 Q50 100 92 86 Q50 74 8 86 Z" fill="#3c3c48" />
    {/* gold trim and a gold star (no skull and crossbones — this is a kids' app) */}
    <path d="M14 80 Q50 68 86 80" fill="none" stroke="#f2c230" strokeWidth="3" />
    <path d="M50 49 L53 57 L61 57 L55 62 L57 70 L50 65 L43 70 L45 62 L39 57 L47 57 Z" fill="#f2c230" />
    <path d="M78 52 Q90 40 86 30 Q80 42 72 48 Z" fill="#ff6b6b" />
  </g>
);

/* ------------------------------------------------------------------ */
/* Glasses — across the band y≈38–64                                    */
/* ------------------------------------------------------------------ */

const CoolShades = () => (
  <g {...S}>
    <path d="M8 44 L92 44" strokeWidth="4" />
    <path d="M12 44 L44 44 L42 58 Q28 66 16 58 Z" fill="#1d1d26" />
    <path d="M56 44 L88 44 L84 58 Q72 66 58 58 Z" fill="#1d1d26" />
    <path d="M20 49 L28 49 M64 49 L72 49" stroke="#7fd3ff" strokeWidth="3" />
  </g>
);

const StarGlasses = () => {
  const star = (cx, cy) =>
    `M${cx} ${cy - 15} L${cx + 4.5} ${cy - 5} L${cx + 15} ${cy - 4} L${cx + 7} ${cy + 3.5} L${cx + 9.5} ${cy + 14} L${cx} ${cy + 8.5} L${cx - 9.5} ${cy + 14} L${cx - 7} ${cy + 3.5} L${cx - 15} ${cy - 4} L${cx - 4.5} ${cy - 5} Z`;
  return (
    <g {...S}>
      <path d={star(29, 50)} fill="#ff5d8f" />
      <path d={star(71, 50)} fill="#ff5d8f" />
      <path d="M44 48 Q50 44 56 48" fill="none" />
      <circle cx="29" cy="50" r="5" fill="#ffd6e5" stroke="none" />
      <circle cx="71" cy="50" r="5" fill="#ffd6e5" stroke="none" />
    </g>
  );
};

const RoundGlasses = () => (
  <g {...S}>
    <circle cx="30" cy="51" r="13" fill="rgba(180,225,255,0.45)" />
    <circle cx="70" cy="51" r="13" fill="rgba(180,225,255,0.45)" />
    <path d="M43 50 Q50 45 57 50 M17 48 L8 44 M83 48 L92 44" fill="none" />
    <path d="M24 45 L28 42 M64 45 L68 42" stroke="#fff" strokeWidth="2.5" />
  </g>
);

/* ------------------------------------------------------------------ */
/* Wings — big, behind the avatar (box is 184% wide, centred)           */
/* ------------------------------------------------------------------ */

const ButterflyWings = () => (
  <g {...S} strokeWidth="2">
    <path d="M50 46 Q30 8 8 18 Q-2 36 20 50 Q2 64 14 80 Q34 86 50 54 Z" fill="#ff9ad1" />
    <path d="M50 46 Q70 8 92 18 Q102 36 80 50 Q98 64 86 80 Q66 86 50 54 Z" fill="#9ad8ff" />
    <circle cx="24" cy="32" r="5" fill="#fff" />
    <circle cx="76" cy="32" r="5" fill="#fff" />
    <circle cx="22" cy="66" r="3.5" fill="#ffd23f" />
    <circle cx="78" cy="66" r="3.5" fill="#ffd23f" />
  </g>
);

const AngelWings = () => {
  const half = "M50 44 Q34 20 6 22 Q14 30 10 34 Q20 38 12 44 Q24 48 16 54 Q30 58 24 64 Q40 62 50 54 Z";
  return (
    <g {...S} strokeWidth="2">
      <path d={half} fill="#ffffff" />
      <path d={half} fill="#ffffff" transform="translate(100 0) scale(-1 1)" />
      <path d="M44 42 Q30 32 18 32 M44 50 Q32 46 22 48" fill="none" stroke="#c9d6ea" />
      <path d="M56 42 Q70 32 82 32 M56 50 Q68 46 78 48" fill="none" stroke="#c9d6ea" />
    </g>
  );
};

const DragonWings = () => {
  const half = "M50 50 L36 16 Q20 14 4 26 L12 34 L6 44 L16 48 L12 58 L26 58 L28 68 Q42 60 50 56 Z";
  return (
    <g {...S} strokeWidth="2">
      <path d={half} fill="#2ec48a" />
      <path d={half} fill="#2ec48a" transform="translate(100 0) scale(-1 1)" />
      <path d="M36 16 L22 52 M36 16 L14 38 M36 16 L30 62" fill="none" stroke="#1a8a5f" />
      <path d="M64 16 L78 52 M64 16 L86 38 M64 16 L70 62" fill="none" stroke="#1a8a5f" />
    </g>
  );
};

/* ------------------------------------------------------------------ */
/* Pets — bottom-right, peeking in                                      */
/* ------------------------------------------------------------------ */

const Puppy = () => (
  <g {...S}>
    <ellipse cx="50" cy="78" rx="30" ry="18" fill="#e8b073" />
    <circle cx="50" cy="48" r="26" fill="#f2c48d" />
    <path d="M26 34 Q14 44 20 66 Q30 60 32 44 Z" fill="#a8693a" />
    <path d="M74 34 Q86 44 80 66 Q70 60 68 44 Z" fill="#a8693a" />
    <circle cx="41" cy="46" r="4" fill={INK} stroke="none" />
    <circle cx="59" cy="46" r="4" fill={INK} stroke="none" />
    <ellipse cx="50" cy="57" rx="6" ry="4.5" fill={INK} stroke="none" />
    <path d="M44 63 Q50 68 56 63" fill="none" />
    <circle cx="42.5" cy="44.5" r="1.3" fill="#fff" stroke="none" />
  </g>
);

const Kitten = () => (
  <g {...S}>
    <ellipse cx="50" cy="80" rx="28" ry="16" fill="#9aa5b8" />
    <path d="M24 30 L30 8 L44 24 Z M76 30 L70 8 L56 24 Z" fill="#9aa5b8" />
    <circle cx="50" cy="48" r="27" fill="#b8c2d4" />
    <path d="M30 14 L34 22 M70 14 L66 22" stroke="#ff9ad1" strokeWidth="3" />
    <ellipse cx="40" cy="46" rx="4" ry="5" fill={INK} stroke="none" />
    <ellipse cx="60" cy="46" rx="4" ry="5" fill={INK} stroke="none" />
    <path d="M47 55 L53 55 L50 59 Z" fill="#ff7aa8" strokeWidth="2" />
    <path d="M50 59 Q46 64 42 61 M50 59 Q54 64 58 61" fill="none" strokeWidth="2" />
    <path d="M28 54 L16 52 M28 58 L17 60 M72 54 L84 52 M72 58 L83 60" strokeWidth="1.8" />
  </g>
);

const BabyDragon = () => (
  <g {...S}>
    <path d="M24 74 Q8 70 6 56 Q16 62 26 62 Z" fill="#2ec48a" />
    <ellipse cx="52" cy="80" rx="28" ry="16" fill="#2ec48a" />
    <ellipse cx="52" cy="84" rx="16" ry="9" fill="#bff0d4" strokeWidth="2" />
    <circle cx="52" cy="46" r="26" fill="#3fd69a" />
    <path d="M34 26 L30 10 L42 22 Z M70 26 L74 10 L62 22 Z" fill="#ffd23f" />
    <circle cx="42" cy="44" r="5" fill="#fff" />
    <circle cx="62" cy="44" r="5" fill="#fff" />
    <circle cx="43" cy="45" r="2.6" fill={INK} stroke="none" />
    <circle cx="63" cy="45" r="2.6" fill={INK} stroke="none" />
    <path d="M44 58 Q52 64 60 58" fill="none" />
    <circle cx="46" cy="53" r="1.4" fill={INK} stroke="none" />
    <circle cx="58" cy="53" r="1.4" fill={INK} stroke="none" />
  </g>
);

/* ------------------------------------------------------------------ */
/* Frames — a ring around the avatar circle (circle = r 40 at 50,50)    */
/* ------------------------------------------------------------------ */

const RainbowRing = () => (
  <g fill="none" strokeWidth="5">
    {["#ff3b3b", "#ff9a3c", "#ffd23f", "#3fd69a", "#2a8bff", "#8b5cf6"].map((c, i) => (
      <circle key={c} cx="50" cy="50" r={47.5 - i * 1.6} stroke={c} strokeWidth="2.2" />
    ))}
  </g>
);

const GoldRing = () => (
  <g>
    <circle cx="50" cy="50" r="45" fill="none" stroke="#d68a00" strokeWidth="7" />
    <circle cx="50" cy="50" r="45" fill="none" stroke="#ffd23f" strokeWidth="4" />
    {[0, 60, 120, 180, 240, 300].map((a) => {
      const r = (a * Math.PI) / 180;
      return <circle key={a} cx={50 + Math.cos(r) * 45} cy={50 + Math.sin(r) * 45} r="3.2" fill="#fff6c7" stroke="#d68a00" strokeWidth="1.2" />;
    })}
  </g>
);

const GalaxyRing = () => (
  <g>
    <defs>
      <linearGradient id="sbl-galaxy" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#5b2bd6" />
        <stop offset="0.5" stopColor="#d63bd0" />
        <stop offset="1" stopColor="#2a8bff" />
      </linearGradient>
    </defs>
    <circle cx="50" cy="50" r="45" fill="none" stroke="url(#sbl-galaxy)" strokeWidth="7" />
    {[[50, 4.5], [88, 30], [92, 62], [70, 92], [24, 88], [8, 56], [16, 22]].map(([x, y], i) => (
      <path
        key={i}
        d={`M${x} ${y - 4} L${x + 1.2} ${y - 1.2} L${x + 4} ${y} L${x + 1.2} ${y + 1.2} L${x} ${y + 4} L${x - 1.2} ${y + 1.2} L${x - 4} ${y} L${x - 1.2} ${y - 1.2} Z`}
        fill="#fff"
      />
    ))}
  </g>
);

/* ------------------------------------------------------------------ */
/* Power-ups (Treasure Quest) — icons, not worn                         */
/* ------------------------------------------------------------------ */

const ExtraMoves = () => (
  <g {...S}>
    <circle cx="50" cy="50" r="40" fill="#2ec4b6" />
    <path d="M30 62 Q28 44 42 40 Q52 38 54 48 L58 64 Q50 72 38 70 Z" fill="#fff" />
    <path d="M36 50 L46 49 M38 57 L50 56" strokeWidth="2" />
    <text x="66" y="46" textAnchor="middle" fontSize="26" fontWeight="900" fill="#ffd23f" stroke={INK} strokeWidth="2">+5</text>
  </g>
);

const Hammer = () => (
  <g {...S}>
    <circle cx="50" cy="50" r="40" fill="#ff9a3c" />
    <path d="M44 46 L66 78 L58 84 L36 52 Z" fill="#c9884c" />
    <path d="M24 34 L52 18 L64 38 L36 54 Z" fill="#9aa5b8" />
    <path d="M28 36 L52 22" stroke="#e6ebf3" strokeWidth="3" />
  </g>
);

const Shuffle = () => (
  <g {...S}>
    <circle cx="50" cy="50" r="40" fill="#8b5cf6" />
    <path d="M28 52 A22 22 0 0 1 66 34" fill="none" stroke="#fff" strokeWidth="7" />
    <path d="M60 24 L72 34 L58 42 Z" fill="#fff" strokeWidth="2" />
    <path d="M72 48 A22 22 0 0 1 34 66" fill="none" stroke="#fff" strokeWidth="7" />
    <path d="M40 76 L28 66 L42 58 Z" fill="#fff" strokeWidth="2" />
  </g>
);

/* ------------------------------------------------------------------ */
/* 🎄 Christmas (season "christmas") — hats, necklaces, pins, pets      */
/* Necklaces are drawn in the necklace box: the collar curve runs from */
/* (18,30) down to (50,58) and up to (82,30).                          */
/* ------------------------------------------------------------------ */

const Antler = ({ flip }) => (
  <g transform={flip ? "translate(100 0) scale(-1 1)" : undefined}>
    <path d="M30 92 C26 70 22 54 16 40 M22 56 C14 52 8 46 6 36 M18 46 C22 38 24 30 22 20 M16 40 C10 32 10 24 12 14" stroke="#8a5a2b" strokeWidth="7" fill="none" strokeLinecap="round" />
    <path d="M30 92 C26 70 22 54 16 40 M22 56 C14 52 8 46 6 36 M18 46 C22 38 24 30 22 20 M16 40 C10 32 10 24 12 14" stroke="#b07a46" strokeWidth="3" fill="none" strokeLinecap="round" />
  </g>
);
const ReindeerAntlers = () => (
  <g>
    <Antler />
    <Antler flip />
    <path d="M14 96 Q50 72 86 96" stroke="#c0392b" strokeWidth="7" fill="none" strokeLinecap="round" />
    <circle cx="50" cy="80" r="6" fill="#ffd23f" stroke={INK} strokeWidth="2.5" />
    <path d="M47 80 h6" stroke={INK} strokeWidth="2" />
    <path d="M34 86 l4 -6 4 6 z M58 86 l4 -6 4 6 z" fill="#2fae5b" stroke={INK} strokeWidth="1.5" />
  </g>
);
const SantaHat = () => (
  <g {...S}>
    <path d="M22 86 C24 50 46 26 74 30 C86 32 92 46 88 58 C80 46 70 46 64 56 C60 66 66 78 72 86 Z" fill="#e23b3b" />
    <rect x="16" y="80" width="68" height="18" rx="9" fill="#fff" />
    <circle cx="88" cy="62" r="9" fill="#fff" />
    <path d="M36 52 Q44 40 56 38" stroke="#ff8a8a" strokeWidth="3" fill="none" />
  </g>
);
const ElfHat = () => (
  <g {...S}>
    <path d="M24 88 C30 60 40 34 58 14 C60 40 70 64 78 88 Z" fill="#2fae5b" />
    <path d="M58 14 C66 8 76 10 80 18" fill="none" stroke="#2fae5b" strokeWidth="7" />
    <circle cx="82" cy="20" r="6" fill="#ffd23f" />
    <path d="M18 88 L82 88 L78 98 L22 98 Z" fill="#e23b3b" />
    <path d="M28 88 l6 10 M42 88 l6 10 M56 88 l6 10 M70 88 l6 10" stroke="#fff" strokeWidth="3" />
  </g>
);
const LIGHTS = ["#ff4d6d", "#ffd23f", "#4cd964", "#3a86ff", "#ff9a3c", "#b07cff"];
const ChristmasLights = ({ gold = false }) => {
  const pts = [];
  for (let i = 0; i <= 8; i += 1) {
    const t = i / 8;
    const x = 18 + t * 64;
    const y = 30 + Math.sin(t * Math.PI) * 28;
    pts.push([x, y, Math.atan2(Math.cos(t * Math.PI) * 28 * Math.PI, 64) * (180 / Math.PI)]);
  }
  return (
    <g>
      <path d="M18 30 Q50 86 82 30" stroke="#1f5c2e" strokeWidth="3" fill="none" />
      {pts.map(([x, y, a], i) => (
        <g key={i} transform={`translate(${x} ${y}) rotate(${a})`}>
          <rect x="-2.6" y="-1" width="5.2" height="4" rx="1" fill="#2a3a2a" />
          <ellipse cx="0" cy="8" rx="4.2" ry="6" fill={gold ? (i % 2 ? "#ffd23f" : "#fff6b0") : LIGHTS[i % LIGHTS.length]} stroke={INK} strokeWidth="1.2" />
          <ellipse cx="-1.4" cy="6" rx="1.2" ry="2" fill="#fff" opacity="0.7" />
          {gold && <circle cx="0" cy="8" r="9" fill="#fff6b0" opacity="0.25" />}
        </g>
      ))}
    </g>
  );
};
const MultiLights = () => <ChristmasLights />;
const GoldLights = () => <ChristmasLights gold />;
const JingleCollar = () => (
  <g>
    <path d="M18 30 Q50 74 82 30" stroke="#c0392b" strokeWidth="8" fill="none" strokeLinecap="round" />
    {[30, 50, 70].map((x, i) => {
      const y = i === 1 ? 54 : 46;
      return (
        <g key={x}>
          <circle cx={x} cy={y + 6} r="6.5" fill="#ffd23f" stroke={INK} strokeWidth="2" />
          <path d={`M${x - 3} ${y + 7} h6`} stroke={INK} strokeWidth="1.6" />
          <circle cx={x - 2} cy={y + 4} r="1.4" fill="#fff" />
        </g>
      );
    })}
  </g>
);
// pins (drawn in the middle of their box)
const HollyPin = () => (
  <g {...S} strokeWidth="4">
    <path d="M50 52 C40 30 22 30 10 34 C18 40 16 48 22 52 C14 58 18 66 28 66 C34 74 44 66 50 52 Z" fill="#2fae5b" />
    <path d="M50 52 C60 30 78 30 90 34 C82 40 84 48 78 52 C86 58 82 66 72 66 C66 74 56 66 50 52 Z" fill="#2fae5b" />
    <circle cx="44" cy="58" r="9" fill="#e23b3b" />
    <circle cx="58" cy="60" r="9" fill="#e23b3b" />
    <circle cx="51" cy="48" r="9" fill="#e23b3b" />
  </g>
);
const CandyCanePin = () => (
  <g>
    <path d="M58 92 L58 34 C58 18 34 18 34 34" stroke={INK} strokeWidth="20" fill="none" strokeLinecap="round" />
    <path d="M58 92 L58 34 C58 18 34 18 34 34" stroke="#fff" strokeWidth="13" fill="none" strokeLinecap="round" />
    <path d="M58 92 L58 34 C58 18 34 18 34 34" stroke="#e23b3b" strokeWidth="13" fill="none" strokeDasharray="7 7" />
  </g>
);
const SnowflakePin = () => (
  <g stroke="#3a86ff" strokeWidth="6" strokeLinecap="round" fill="none">
    {[0, 60, 120].map((a) => (
      <g key={a} transform={`rotate(${a} 50 50)`}>
        <path d="M50 10 L50 90 M50 22 L40 14 M50 22 L60 14 M50 78 L40 86 M50 78 L60 86" />
      </g>
    ))}
    <circle cx="50" cy="50" r="6" fill="#bfe3ff" />
  </g>
);
const GingerbreadPin = () => (
  <g {...S}>
    <path d="M50 10 C62 10 66 22 62 30 L84 38 C90 42 86 52 80 50 L64 46 L70 84 C72 92 62 94 58 88 L50 70 L42 88 C38 94 28 92 30 84 L36 46 L20 50 C14 52 10 42 16 38 L38 30 C34 22 38 10 50 10 Z" fill="#c07a3a" />
    <circle cx="45" cy="22" r="2.6" fill={INK} />
    <circle cx="55" cy="22" r="2.6" fill={INK} />
    <path d="M45 28 Q50 32 55 28" fill="none" />
    <circle cx="50" cy="44" r="3.4" fill="#e23b3b" />
    <circle cx="50" cy="56" r="3.4" fill="#2fae5b" />
    <path d="M22 44 l4 -4 M78 44 l-4 -4" stroke="#fff" strokeWidth="3" />
  </g>
);
const PresentPin = () => (
  <g {...S}>
    <rect x="18" y="40" width="64" height="50" rx="6" fill="#3a86ff" />
    <rect x="14" y="30" width="72" height="16" rx="5" fill="#5aa2ff" />
    <rect x="44" y="30" width="12" height="60" fill="#ffd23f" />
    <path d="M50 30 C40 10 24 18 36 30 Z M50 30 C60 10 76 18 64 30 Z" fill="#ffd23f" />
  </g>
);
// pets
const BabyReindeer = () => (
  <g {...S}>
    <path d="M30 22 L24 6 M24 14 L16 10 M70 22 L76 6 M76 14 L84 10" stroke="#8a5a2b" strokeWidth="5" />
    <ellipse cx="50" cy="78" rx="26" ry="18" fill="#a5683a" />
    <ellipse cx="50" cy="44" rx="24" ry="22" fill="#b97a46" />
    <ellipse cx="26" cy="34" rx="8" ry="5" fill="#b97a46" transform="rotate(-30 26 34)" />
    <ellipse cx="74" cy="34" rx="8" ry="5" fill="#b97a46" transform="rotate(30 74 34)" />
    <ellipse cx="50" cy="56" rx="12" ry="9" fill="#e8c49a" />
    <circle cx="50" cy="52" r="6" fill="#4a2c1c" />
    <circle cx="48" cy="50" r="1.8" fill="#fff" stroke="none" />
    <circle cx="41" cy="40" r="3.4" fill={INK} stroke="none" />
    <circle cx="59" cy="40" r="3.4" fill={INK} stroke="none" />
  </g>
);
const SnowmanBuddy = () => (
  <g {...S}>
    <circle cx="50" cy="74" r="22" fill="#fff" />
    <circle cx="50" cy="38" r="17" fill="#fff" />
    <rect x="36" y="14" width="28" height="8" rx="2" fill="#2a2130" />
    <rect x="40" y="0" width="20" height="16" rx="2" fill="#2a2130" />
    <path d="M50 40 L64 43 L50 45 Z" fill="#ff9a3c" />
    <circle cx="44" cy="34" r="2.4" fill={INK} stroke="none" />
    <circle cx="56" cy="34" r="2.4" fill={INK} stroke="none" />
    <path d="M34 52 Q50 60 66 52 L66 58 Q50 66 34 58 Z" fill="#e23b3b" />
    <circle cx="50" cy="70" r="2.4" fill={INK} stroke="none" />
    <circle cx="50" cy="80" r="2.4" fill={INK} stroke="none" />
  </g>
);
const SnowflakeRing = () => (
  <g>
    <circle cx="50" cy="50" r="46" fill="none" stroke="#bfe3ff" strokeWidth="7" />
    <circle cx="50" cy="50" r="46" fill="none" stroke="#3a86ff" strokeWidth="2" strokeDasharray="4 8" />
    {Array.from({ length: 8 }, (_, i) => (
      <g key={i} transform={`rotate(${i * 45} 50 50) translate(50 4)`}>
        <path d="M0 -5 L0 5 M-4 -3 L4 3 M-4 3 L4 -3" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" />
      </g>
    ))}
  </g>
);

export const ART = {
  "extra-moves": ExtraMoves,
  hammer: Hammer,
  shuffle: Shuffle,
  "party-hat": PartyHat,
  "baseball-cap": BaseballCap,
  crown: Crown,
  "wizard-hat": WizardHat,
  "pirate-hat": PirateHat,
  "cool-shades": CoolShades,
  "star-glasses": StarGlasses,
  "round-glasses": RoundGlasses,
  "butterfly-wings": ButterflyWings,
  "angel-wings": AngelWings,
  "dragon-wings": DragonWings,
  puppy: Puppy,
  kitten: Kitten,
  "baby-dragon": BabyDragon,
  "rainbow-ring": RainbowRing,
  "gold-ring": GoldRing,
  "galaxy-ring": GalaxyRing,
  "reindeer-antlers": ReindeerAntlers,
  "santa-hat": SantaHat,
  "elf-hat": ElfHat,
  "xmas-lights": MultiLights,
  "xmas-lights-gold": GoldLights,
  "jingle-collar": JingleCollar,
  "holly-pin": HollyPin,
  "candy-cane-pin": CandyCanePin,
  "snowflake-pin": SnowflakePin,
  "gingerbread-pin": GingerbreadPin,
  "present-pin": PresentPin,
  "baby-reindeer": BabyReindeer,
  "snowman-buddy": SnowmanBuddy,
  "snowflake-ring": SnowflakeRing,
};

// Holiday windows used by the starter set (admins can change any item's
// dates in Rewards & avatar settings → Shop items).
const XMAS = { season: "christmas", availableFrom: "12-01", availableUntil: "01-06" };

/** The starter catalogue — written to shopItems by the admin panel. */
export const STARTER_ITEMS = [
  { id: "hat-party", name: "Party Hat", kind: "hat", art: "party-hat", price: 0, sort: 1 },
  { id: "hat-cap", name: "SBL Cap", kind: "hat", art: "baseball-cap", price: 150, sort: 2 },
  { id: "hat-pirate", name: "Pirate Hat", kind: "hat", art: "pirate-hat", price: 300, sort: 3 },
  { id: "hat-wizard", name: "Wizard Hat", kind: "hat", art: "wizard-hat", price: 500, sort: 4 },
  { id: "hat-crown", name: "Royal Crown", kind: "hat", art: "crown", price: 900, sort: 5 },
  { id: "glasses-round", name: "Smart Glasses", kind: "glasses", art: "round-glasses", price: 0, sort: 1 },
  { id: "glasses-shades", name: "Cool Shades", kind: "glasses", art: "cool-shades", price: 200, sort: 2 },
  { id: "glasses-star", name: "Star Glasses", kind: "glasses", art: "star-glasses", price: 300, sort: 3 },
  { id: "wings-butterfly", name: "Butterfly Wings", kind: "wings", art: "butterfly-wings", price: 800, sort: 1 },
  { id: "wings-angel", name: "Cloud Wings", kind: "wings", art: "angel-wings", price: 1100, sort: 2 },
  { id: "wings-dragon", name: "Dragon Wings", kind: "wings", art: "dragon-wings", price: 1600, sort: 3 },
  { id: "pet-puppy", name: "Puppy Pal", kind: "pet", art: "puppy", price: 700, sort: 1 },
  { id: "pet-kitten", name: "Kitten Pal", kind: "pet", art: "kitten", price: 700, sort: 2 },
  { id: "pet-dragon", name: "Baby Dragon", kind: "pet", art: "baby-dragon", price: 1800, sort: 3 },
  { id: "frame-rainbow", name: "Rainbow Ring", kind: "frame", art: "rainbow-ring", price: 0, sort: 1 },
  { id: "frame-gold", name: "Gold Ring", kind: "frame", art: "gold-ring", price: 400, sort: 2 },
  { id: "frame-galaxy", name: "Galaxy Ring", kind: "frame", art: "galaxy-ring", price: 700, sort: 3 },
  // Treasure Quest power-ups — used up, buy again any time (max 9 held)
  { id: "pu-moves", name: "+5 Moves", kind: "powerup", art: "extra-moves", price: 60, sort: 1, effect: "moves" },
  { id: "pu-hammer", name: "Hammer", kind: "powerup", art: "hammer", price: 50, sort: 2, effect: "hammer" },
  { id: "pu-shuffle", name: "Shuffle", kind: "powerup", art: "shuffle", price: 30, sort: 3, effect: "shuffle" },
  // 🎄 Christmas — on sale Dec 1 – Jan 6 every year (MM-DD)
  { id: "xmas-antlers", name: "Reindeer Antlers", kind: "hat", art: "reindeer-antlers", price: 400, sort: 20, ...XMAS },
  { id: "xmas-santa-hat", name: "Santa Hat", kind: "hat", art: "santa-hat", price: 350, sort: 21, ...XMAS },
  { id: "xmas-elf-hat", name: "Elf Hat", kind: "hat", art: "elf-hat", price: 300, sort: 22, ...XMAS },
  { id: "xmas-lights", name: "Christmas Lights", kind: "necklace", art: "xmas-lights", price: 450, sort: 1, ...XMAS },
  { id: "xmas-lights-gold", name: "Golden Twinkle Lights", kind: "necklace", art: "xmas-lights-gold", price: 600, sort: 2, ...XMAS },
  { id: "xmas-jingle", name: "Jingle Bell Collar", kind: "necklace", art: "jingle-collar", price: 350, sort: 3, ...XMAS },
  { id: "xmas-holly", name: "Holly Leaf Pin", kind: "pin", art: "holly-pin", price: 150, sort: 1, ...XMAS },
  { id: "xmas-candy-cane", name: "Candy Cane Pin", kind: "pin", art: "candy-cane-pin", price: 120, sort: 2, ...XMAS },
  { id: "xmas-snowflake", name: "Snowflake Pin", kind: "pin", art: "snowflake-pin", price: 120, sort: 3, ...XMAS },
  { id: "xmas-gingerbread", name: "Gingerbread Pin", kind: "pin", art: "gingerbread-pin", price: 180, sort: 4, ...XMAS },
  { id: "xmas-present", name: "Present Pin", kind: "pin", art: "present-pin", price: 150, sort: 5, ...XMAS },
  { id: "xmas-reindeer", name: "Baby Reindeer", kind: "pet", art: "baby-reindeer", price: 1200, sort: 10, ...XMAS },
  { id: "xmas-snowman", name: "Snowman Buddy", kind: "pet", art: "snowman-buddy", price: 900, sort: 11, ...XMAS },
  { id: "xmas-snow-ring", name: "Snowflake Ring", kind: "frame", art: "snowflake-ring", price: 500, sort: 10, ...XMAS },
];

/** Draws one item (SVG art, or its picture) filling its slot box. */
export const ItemArt = ({ item, className = "" }) => {
  if (!item) return null;
  if (item.imageUrl) {
    return <img className={`sbl-item-img sbl-item-${item.kind} ${className}`} src={item.imageUrl} alt="" draggable={false} />;
  }
  const Art = ART[item.art] || SEASON_ART[item.art];
  if (!Art) return null;
  return (
    <svg className={className} viewBox="0 0 100 100" overflow="visible" aria-hidden="true">
      <Art />
    </svg>
  );
};

/**
 * Is a seasonal item on sale on `date`? availableFrom/availableUntil are
 * "MM-DD" (every year) or "YYYY-MM-DD" (one year only). A window that wraps
 * the new year (12-01 → 01-06) works. No dates = always on sale.
 */
export const inSeason = (item, date = new Date()) => {
  const from = item?.availableFrom;
  const until = item?.availableUntil;
  if (!from && !until) return true;
  const ny = new Date(date.toLocaleString("en-US", { timeZone: "America/New_York" }));
  const pad = (n) => String(n).padStart(2, "0");
  const md = `${pad(ny.getMonth() + 1)}-${pad(ny.getDate())}`;
  const ymd = `${ny.getFullYear()}-${md}`;
  if (String(from || "").length > 5 || String(until || "").length > 5) return (!from || ymd >= from) && (!until || ymd <= until);
  const f = from || "01-01";
  const u = until || "12-31";
  return f <= u ? md >= f && md <= u : md >= f || md <= u; // e.g. 12-01 → 01-06 wraps the new year
};

export const SEASON_LABELS = { christmas: "🎄 Christmas", thanksgiving: "🦃 Thanksgiving", halloween: "🎃 Halloween", valentines: "💝 Valentine's", easter: "🐣 Spring", summer: "☀️ Summer", winter: "❄️ Winter", birthday: "🎂 Birthday" };
